// Retry utility with exponential backoff for handling rate limiting and network errors

interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  retryCondition?: (error: any) => boolean;
}

interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: any;
  attempts: number;
}

class RetryUtils {
  private static defaultOptions: Required<RetryOptions> = {
    maxRetries: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    backoffFactor: 2,
    retryCondition: (error: any) => {
      // Don't retry on CSP violations - these are configuration issues
      if (error.message && error.message.includes('Content Security Policy')) {
        return false;
      }
      
      // Retry on network errors, 429 (rate limit), 502, 503, 504
      return (
        !error.response || 
        error.response.status === 429 ||
        error.response.status === 502 ||
        error.response.status === 503 ||
        error.response.status === 504 ||
        error.code === 'NETWORK_ERROR' ||
        error.code === 'TIMEOUT'
      );
    }
  };

  /**
   * Execute a function with retry logic and exponential backoff
   */
  static async executeWithRetry<T>(
    fn: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<RetryResult<T>> {
    const opts = { ...this.defaultOptions, ...options };
    let lastError: any;
    
    for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
      try {
        const result = await fn();
        return {
          success: true,
          data: result,
          attempts: attempt + 1
        };
      } catch (error) {
        lastError = error;
        
        // Don't retry if condition is not met
        if (!opts.retryCondition(error)) {
          break;
        }
        
        // Don't wait after the last attempt
        if (attempt < opts.maxRetries) {
          const delay = this.calculateDelay(attempt, opts);
          console.warn(`[RetryUtils] Attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error);
          await this.sleep(delay);
        }
      }
    }
    
    return {
      success: false,
      error: lastError,
      attempts: opts.maxRetries + 1
    };
  }

  /**
   * Calculate delay with exponential backoff and jitter
   */
  private static calculateDelay(attempt: number, options: Required<RetryOptions>): number {
    const exponentialDelay = options.baseDelay * Math.pow(options.backoffFactor, attempt);
    const jitter = Math.random() * 0.1 * exponentialDelay; // Add 10% jitter
    const delay = Math.min(exponentialDelay + jitter, options.maxDelay);
    return Math.floor(delay);
  }

  /**
   * Sleep for specified milliseconds
   */
  private static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Create a retry wrapper for fetch requests
   */
  static createRetryFetch(options: RetryOptions = {}) {
    return async (url: string, fetchOptions: RequestInit = {}): Promise<Response> => {
      const result = await this.executeWithRetry(async () => {
        const response = await fetch(url, fetchOptions);
        
        // Treat non-2xx responses as errors for retry logic
        if (!response.ok) {
          const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
          (error as any).response = response;
          throw error;
        }
        
        return response;
      }, options);
      
      if (!result.success) {
        throw result.error;
      }
      
      return result.data!;
    };
  }

  /**
   * Create a retry wrapper for axios requests
   */
  static createRetryAxios(axiosInstance: any, options: RetryOptions = {}) {
    return async (config: any): Promise<any> => {
      const result = await this.executeWithRetry(async () => {
        return await axiosInstance(config);
      }, options);
      
      if (!result.success) {
        throw result.error;
      }
      
      return result.data;
    };
  }

  /**
   * Get retry delay for rate limit headers
   */
  static getRetryAfterDelay(response: Response): number {
    const retryAfter = response.headers.get('Retry-After');
    if (retryAfter) {
      const seconds = parseInt(retryAfter, 10);
      if (!isNaN(seconds)) {
        return seconds * 1000; // Convert to milliseconds
      }
    }
    return 0;
  }

  /**
   * Check if error is rate limiting related
   */
  static isRateLimitError(error: any): boolean {
    return (
      error.response?.status === 429 ||
      error.message?.includes('rate limit') ||
      error.message?.includes('too many requests')
    );
  }

  /**
   * Get user-friendly error message for rate limiting
   */
  static getRateLimitMessage(error: any): string {
    if (this.isRateLimitError(error)) {
      const retryAfter = this.getRetryAfterDelay(error.response);
      if (retryAfter > 0) {
        const seconds = Math.ceil(retryAfter / 1000);
        return `Rate limit exceeded. Please wait ${seconds} seconds before trying again.`;
      }
      return 'Rate limit exceeded. Please wait a moment before trying again.';
    }
    
    // Handle CSP violations
    if (error.message && error.message.includes('Content Security Policy')) {
      return 'Connection blocked by browser security policy. Please check your network configuration.';
    }
    
    return error.message || 'An error occurred. Please try again.';
  }
}

export default RetryUtils;

